"use client";

import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import { useState } from "react";
import {
  Container,
  Content,
  ContentWrapper,
  Title,
  FAQItem,
  Question,
  Answer,
  Category,
  CTABox,
  CTAButton,
} from "./page.styles";

export default function ArtisanFAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      category: "Getting Started",
      items: [
        {
          q: "How do I become an artisan on Drizl?",
          a: "Sign up for an artisan account, and set up your profile and menu.",
        },
        {
          q: "What are the requirements to become an artisan?",
          a: "You must be 18+, have a clean kitchen, follow local food safety regulations, and comply with any required permits or certifications in your area.",
        },
        {
          q: "Do I need any permits or licenses?",
          a: "Requirements vary by location. Check your local health department for cottage food laws and home-based food business regulations.",
        },
        {
          q: "Is there an application fee?",
          a: "No, it's free to create an artisan account and list your meals on Drizl.",
        },
      ],
    },
    {
      category: "Menu & Pricing",
      items: [
        {
          q: "How do I add meals to my menu?",
          a: "From your artisan dashboard, click 'Add Meal', upload photos, write descriptions, set prices, and specify dietary information.",
        },
        {
          q: "Can I change my prices?",
          a: "Yes, you can update prices anytime from your dashboard. New prices apply to future orders only.",
        },
        {
          q: "How should I price my meals?",
          a: "Consider ingredient costs, preparation time, and local market rates. Check similar artisans for competitive pricing.",
        },
        {
          q: "How many meals should I offer?",
          a: "Start with 3-5 of your best dishes. You can always add more as you grow.",
        },
      ],
    },
    {
      category: "Orders & Operations",
      items: [
        {
          q: "How do I receive orders?",
          a: "You'll get notifications via email when customers place orders. You can accept or decline based on your availability.",
        },
        {
          q: "Can I set my own schedule?",
          a: "Yes! Control when you accept orders, set preparation times, and manage your availability through your dashboard.",
        },
        {
          q: "What if I can't fulfill an order?",
          a: "Communicate with the customer immediately and cancel if necessary. Frequent cancellations may affect your rating.",
        },
        {
          q: "How do I handle special requests?",
          a: "Communicate through the app's messaging feature. Accept requests you can accommodate and politely decline others.",
        },
      ],
    },
    {
      category: "Payments & Earnings",
      items: [
        {
          q: "How do I get paid?",
          a: "Payments are transferred to your bank account after successful order completion. Standard transfer time is 2-3 business days.",
        },
        {
          q: "What fees does Drizl charge?",
          a: "Drizl takes a service fee from each order to cover platform costs and payment processing. See your dashboard for current rates.",
        },
      ],
    },
    {
      category: "Food Safety & Quality",
      items: [
        {
          q: "What food safety standards must I follow?",
          a: "Maintain clean kitchen conditions, proper food storage, safe handling practices, and comply with all local health regulations. See our Safety Guidelines page.",
        },
        {
          q: "How do I handle food allergies?",
          a: "Clearly list all allergens in meal descriptions. Never minimize allergy risks. Refuse orders if you can't safely accommodate allergies.",
        },
        {
          q: "What packaging should I use?",
          a: "Use food-safe, sealed containers appropriate for the meal type. Consider eco-friendly options when possible.",
        },
      ],
    },
    {
      category: "Growing Your Business",
      items: [
        {
          q: "How do I get more orders?",
          a: "Take high-quality photos, write detailed descriptions, maintain excellent ratings, respond quickly to inquiries, and encourage reviews.",
        },
        {
          q: "Can I promote my meals?",
          a: "Yes! Share your Drizl profile on social media, offer new customer promotions, and engage with the community feed.",
        },
        {
          q: "How important are reviews?",
          a: "Very important! Positive reviews build trust and attract more customers. Always deliver quality and respond professionally to feedback.",
        },
      ],
    },
  ];

  return (
    <Container>
      <PublicHeader />
      <Content>
        <ContentWrapper>
          <Title>Artisan FAQ</Title>
          <CTABox>
            <h3 style={{ fontSize: "1.75rem", marginBottom: "0.5rem" }}>
              Ready to Start Your Culinary Journey?
            </h3>
            <p style={{ marginBottom: "0.5rem" }}>
              Turn your passion for cooking into income
            </p>
            <CTAButton href="/artisan-onboarding">Become an Artisan</CTAButton>
          </CTABox>

          {faqs.map((category, catIndex) => (
            <div key={catIndex}>
              <Category>{category.category}</Category>
              {category.items.map((item, itemIndex) => {
                const globalIndex = catIndex * 100 + itemIndex;
                return (
                  <FAQItem key={globalIndex}>
                    <Question
                      onClick={() =>
                        setOpenIndex(
                          openIndex === globalIndex ? null : globalIndex
                        )
                      }
                    >
                      {item.q}
                      <span>{openIndex === globalIndex ? "−" : "+"}</span>
                    </Question>
                    <Answer $isOpen={openIndex === globalIndex}>
                      {item.a}
                    </Answer>
                  </FAQItem>
                );
              })}
            </div>
          ))}
        </ContentWrapper>
      </Content>
      <Footer />
    </Container>
  );
}
